import { useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { queryClient } from "@/shared/api/queryClient";
import { apiErrorMessage } from "@/shared/api/apiError";
import type { ResyncScope } from "@/shared/interfaces/ExternalUserMapping";
import {
  bodycamMappingKeys,
  stationMappingsQuery,
  vendorDirectoryQuery,
} from "../api/bodycamMappingsQueries";
import * as api from "../api/bodycamMappingsApi";

/**
 * Reads and writes for one station's bodycam user mappings. Both reads need
 * `users:admin`, so a caller without it gets an error state rather than an
 * empty roster — never render "no mappings" from a failed load.
 */
export function useUserMappings(stationId: string) {
  const mappingsQuery = useQuery({
    ...stationMappingsQuery(stationId),
    enabled: !!stationId,
  });

  const directoryQuery = useQuery({
    ...vendorDirectoryQuery(stationId),
    enabled: !!stationId,
  });

  const invalidateMappings = useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: bodycamMappingKeys.station(stationId),
        // The directory is a 10s vendor scan and is unaffected by our writes.
        predicate: (query) => query.queryKey.includes("mappings"),
      }),
    [stationId],
  );

  const createMapping = useCallback(
    async (payload: { externalUserId: string; userId: string }) => {
      const mapping = await api.createMapping(stationId, {
        ...payload,
        active: true,
      });
      await invalidateMappings();
      return mapping;
    },
    [stationId, invalidateMappings],
  );

  const reassignMapping = useCallback(
    async (externalUserId: string, userId: string) => {
      const mapping = await api.updateMapping(stationId, externalUserId, {
        userId,
        active: true,
      });
      await invalidateMappings();
      return mapping;
    },
    [stationId, invalidateMappings],
  );

  const deleteMapping = useCallback(
    async (externalUserId: string) => {
      await api.deleteMapping(stationId, externalUserId);
      await invalidateMappings();
    },
    [stationId, invalidateMappings],
  );

  const triggerResync = useCallback(
    async (scope: ResyncScope = "unmapped_only") => {
      const result = await api.triggerResync(stationId, scope);
      await invalidateMappings();
      return result;
    },
    [stationId, invalidateMappings],
  );

  const refreshDirectory = useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: bodycamMappingKeys.directory(stationId),
      }),
    [stationId],
  );

  return {
    mappings: mappingsQuery.data ?? [],
    isLoadingMappings: mappingsQuery.isPending,
    // The `?? []` above is only safe once a load has succeeded. With nothing
    // cached to fall back on, callers must render this instead of an empty
    // mapping list — see the note on this hook.
    mappingsLoadFailed:
      mappingsQuery.isError &&
      !mappingsQuery.isFetching &&
      mappingsQuery.data === undefined,
    mappingsError: mappingsQuery.isError
      ? apiErrorMessage(mappingsQuery.error, "Failed to load user mappings")
      : null,
    refreshMappings: invalidateMappings,

    directory: directoryQuery.data,
    isLoadingDirectory: directoryQuery.isPending || directoryQuery.isFetching,
    // Only the retryable failures land here; "no roster for this station" is a
    // successful `unavailable` entry instead.
    directoryError: directoryQuery.isError
      ? apiErrorMessage(
          directoryQuery.error,
          "Couldn't load the vendor officer directory.",
        )
      : null,
    refreshDirectory,

    createMapping,
    reassignMapping,
    deleteMapping,
    triggerResync,
  };
}
